import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/Report_attributes_SalvarAtributosCategoriaInput';
import '../contracts/Report_attributes_SalvarInput';
import '../contracts/Report_attributes_SalvarOpcaoInput';

import '../contracts/Report_attributes_AtributosCategoriaResponse';
import '../contracts/Report_attributes_ListaResponse';
import '../contracts/Report_attributes_OpcoesResponse';
import '../contracts/Report_attributes_RemoverOpcaoResponse';
import '../contracts/Report_attributes_RemoverResponse';
import '../contracts/Report_attributes_SalvarAtributosCategoriaResponse';
import '../contracts/Report_attributes_SalvarOpcaoResponse';
import '../contracts/Report_attributes_SalvarResponse';
import type { Report_attributes_SalvarAtributosCategoriaInput } from '../contracts/Report_attributes_SalvarAtributosCategoriaInput';
import type { Report_attributes_SalvarInput } from '../contracts/Report_attributes_SalvarInput';
import type { Report_attributes_SalvarOpcaoInput } from '../contracts/Report_attributes_SalvarOpcaoInput';
export function useSOReportAttributes() {
  const isLoading = ref(false);
  const error = ref(null);
  const basePath = '/index.php/so/report_attributes';

  const fetchAll = async () => {
    log('useSOReportAttributes', 'fetchAll', 'Fetching all report attributes');
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/lista`, { responseContract: 'Report_attributes_ListaResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar atributos de laudo');
    } catch (err) {
      logError('useSOReportAttributes', 'fetchAll', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const save = async (data: Report_attributes_SalvarInput) => {
    log('useSOReportAttributes', 'save', 'Saving report attribute', data);
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.post(`${basePath}/salvar`, data, { inputContract: 'Report_attributes_SalvarInput', responseContract: 'Report_attributes_SalvarResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao salvar atributo de laudo');
    } catch (err) {
      logError('useSOReportAttributes', 'save', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const remove = async (id: any) => {
    log('useSOReportAttributes', 'remove', 'Removing report attribute', { id });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.post(`${basePath}/remover/${id}`, null, { responseContract: 'Report_attributes_RemoverResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao remover atributo de laudo');
    } catch (err) {
      logError('useSOReportAttributes', 'remove', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchOptions = async (attributeId: number) => {
    log('useSOReportAttributes', 'fetchOptions', 'Fetching report attribute options', { attributeId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/opcoes/${attributeId}`, { responseContract: 'Report_attributes_OpcoesResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar opcoes do atributo de laudo');
    } catch (err) {
      logError('useSOReportAttributes', 'fetchOptions', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const saveOption = async (data: Report_attributes_SalvarOpcaoInput) => {
    log('useSOReportAttributes', 'saveOption', 'Saving report attribute option', data);
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.post(`${basePath}/salvar_opcao`, data, { inputContract: 'Report_attributes_SalvarOpcaoInput', responseContract: 'Report_attributes_SalvarOpcaoResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao salvar opcao do atributo de laudo');
    } catch (err) {
      logError('useSOReportAttributes', 'saveOption', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const removeOption = async (optionId: number) => {
    log('useSOReportAttributes', 'removeOption', 'Removing report attribute option', { optionId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.post(`${basePath}/remover_opcao/${optionId}`, null, { responseContract: 'Report_attributes_RemoverOpcaoResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao remover opcao do atributo de laudo');
    } catch (err) {
      logError('useSOReportAttributes', 'removeOption', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchCategoryAttributes = async (categoryId: number) => {
    log('useSOReportAttributes', 'fetchCategoryAttributes', 'Fetching category report attributes', { categoryId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/atributos_categoria/${categoryId}`, { responseContract: 'Report_attributes_AtributosCategoriaResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar atributos de laudo da categoria');
    } catch (err) {
      logError('useSOReportAttributes', 'fetchCategoryAttributes', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const saveCategoryAttributes = async (categoryId: any, attributes: any) => {
    const payload: Report_attributes_SalvarAtributosCategoriaInput = { attributes };
    log('useSOReportAttributes', 'saveCategoryAttributes', 'Saving category report attributes', {
      categoryId,
      payload
    });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.post(`${basePath}/salvar_atributos_categoria/${categoryId}`, payload, { inputContract: 'Report_attributes_SalvarAtributosCategoriaInput', responseContract: 'Report_attributes_SalvarAtributosCategoriaResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao salvar atributos de laudo da categoria');
    } catch (err) {
      logError('useSOReportAttributes', 'saveCategoryAttributes', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    fetchAll,
    save,
    remove,
    fetchOptions,
    saveOption,
    removeOption,
    fetchCategoryAttributes,
    saveCategoryAttributes
  };
}
